/**
 * A timeline paginada de um cliente (H08).
 *
 * ── Por que não usar o bloco que o 360 já traz ────────────────────────────
 *
 * O `/v1/customers/{id}/360` devolve a primeira página da timeline junto com
 * tudo, e é o certo: uma requisição pinta a tela (§11.2). Mas ele não filtra por
 * tipo, e o filtro é requisito da task.
 *
 * Então o bloco do 360 serve a primeira página, e o `/v1/timeline` assume quando
 * o usuário filtra ou pede mais. Manter as duas fontes é menos código do que
 * parece — a forma do item é a MESMA nas duas rotas, de propósito: o cabeçalho
 * do contrato registra que duas formas para a mesma coisa é como elas divergem.
 *
 * ── Abertura progressiva de verdade ───────────────────────────────────────
 *
 * *"Cliente com dois anos de histórico não carrega inteiro."* O cursor caminha
 * para o passado, e cada página se acumula sem rebuscar as anteriores — fato
 * antigo não muda, é a mesma propriedade que sustenta o histórico da conversa.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ItemDaTimeline, TipoDeItemDaTimeline } from '@cliqcrm/contracts';
import { useApi } from '@/shared/api/consultas';

export interface TimelineDoCliente {
  readonly itens: readonly ItemDaTimeline[];
  readonly temMais: boolean;
  readonly carregando: boolean;
  readonly erro: unknown;
  readonly carregarMais: () => void;
  /** `true` quando o filtro está ativo e a busca substituiu a página do 360. */
  readonly filtrada: boolean;
}

export function useTimelineDoCliente(
  clienteId: string | null,
  tipos: readonly TipoDeItemDaTimeline[],
  /** A primeira página que o 360 já trouxe, usada quando não há filtro. */
  doBloco: { itens: readonly ItemDaTimeline[]; cursor: string | null; temMais: boolean } | null,
): TimelineDoCliente {
  const api = useApi();
  const filtrada = tipos.length > 0;
  const chaveDosTipos = [...tipos].sort().join(',');

  const [paginas, definirPaginas] = useState<readonly ItemDaTimeline[]>([]);
  const [cursor, definirCursor] = useState<string | null>(null);
  const [temMaisAdiante, definirTemMaisAdiante] = useState(false);
  const [carregando, definirCarregando] = useState(false);
  const [erro, definirErro] = useState<unknown>(null);

  // Trocar de cliente ou de filtro zera o acumulado: são outras páginas.
  useEffect(() => {
    definirPaginas([]);
    definirCursor(null);
    definirTemMaisAdiante(false);
    definirErro(null);
  }, [clienteId, chaveDosTipos]);

  const emCurso = useRef(false);

  const buscar = useCallback(
    (cursorAtual: string | null) => {
      if (!api || !clienteId || emCurso.current) return;

      emCurso.current = true;
      definirCarregando(true);
      definirErro(null);

      void api
        .listarTimeline({
          consulta: {
            cliente: clienteId,
            ...(filtrada ? { tipo: chaveDosTipos } : {}),
            ...(cursorAtual ? { cursor: cursorAtual } : {}),
          },
        })
        .then((pagina) => {
          definirPaginas((atuais) => [...atuais, ...pagina.itens]);
          definirCursor(pagina.pagina.cursor_proximo);
          definirTemMaisAdiante(pagina.pagina.tem_mais);
        })
        .catch((falha: unknown) => definirErro(falha))
        .finally(() => {
          emCurso.current = false;
          definirCarregando(false);
        });
    },
    [api, clienteId, filtrada, chaveDosTipos],
  );

  /**
   * Com filtro, a primeira busca é imediata — o bloco do 360 não filtra.
   *
   * Sem filtro, nada é buscado: a página que já veio basta, e uma requisição a
   * mais só para repetir o que está na mão é o tipo de desperdício que não
   * aparece em teste nenhum.
   */
  useEffect(() => {
    if (filtrada && clienteId) buscar(null);
  }, [filtrada, clienteId, chaveDosTipos, buscar]);

  const itens = useMemo(() => {
    if (filtrada) return paginas;
    return [...(doBloco?.itens ?? []), ...paginas];
  }, [filtrada, paginas, doBloco]);

  const temMais = useMemo(() => {
    if (filtrada) return temMaisAdiante;
    // Enquanto nada foi acrescentado, quem sabe se há mais é o bloco do 360.
    return paginas.length === 0 ? (doBloco?.temMais ?? false) : temMaisAdiante;
  }, [filtrada, paginas.length, temMaisAdiante, doBloco]);

  const carregarMais = useCallback(() => {
    const proximo = paginas.length === 0 && !filtrada ? (doBloco?.cursor ?? null) : cursor;
    if (proximo) buscar(proximo);
  }, [paginas.length, filtrada, doBloco, cursor, buscar]);

  return { itens, temMais, carregando, erro, carregarMais, filtrada };
}
